export default function initBentoFeatures() {
  document.querySelectorAll("[data-bento-features]").forEach((block) => {
    const grid = block.querySelector("[data-bento-grid]");
    const spotlight = block.querySelector("[data-bento-spotlight]");
    const cards = block.querySelectorAll("[data-bento-card]");

    if (!grid || !spotlight || !cards.length) return;

    let animationFrame = null;
    let previousTime = null;
    let currentSpotlight = null;
    let targetSpotlight = null;
    let isBlockVisible = false;
    let isTrackingPointer = false;
    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const desktopQuery = window.matchMedia("(min-width: 1000px)");

    const clamp = (value, minimum, maximum) => {
      if (minimum > maximum) return (minimum + maximum) / 2;
      return Math.min(Math.max(value, minimum), maximum);
    };

    const renderSpotlight = ({ x, y, width, height }) => {
      grid.style.setProperty("--spotlight-x", `${x}px`);
      grid.style.setProperty("--spotlight-y", `${y}px`);
      grid.style.setProperty("--spotlight-width", `${width}px`);
      grid.style.setProperty("--spotlight-height", `${height}px`);
    };

    const animateSpotlight = (time) => {
      if (!currentSpotlight || !targetSpotlight) {
        animationFrame = null;
        previousTime = null;
        return;
      }

      const elapsed = previousTime === null ? 16 : Math.min(time - previousTime, 32);
      const easing = 1 - Math.exp(-elapsed / 260);
      previousTime = time;

      Object.keys(currentSpotlight).forEach((property) => {
        currentSpotlight[property] += (targetSpotlight[property] - currentSpotlight[property]) * easing;
      });

      renderSpotlight(currentSpotlight);

      const remainingDistance = Object.keys(currentSpotlight).reduce(
        (largest, property) => Math.max(largest, Math.abs(targetSpotlight[property] - currentSpotlight[property])),
        0,
      );

      if (remainingDistance > 0.1) {
        animationFrame = window.requestAnimationFrame(animateSpotlight);
      } else {
        currentSpotlight = { ...targetSpotlight };
        renderSpotlight(currentSpotlight);
        animationFrame = null;
        previousTime = null;
      }
    };

    const moveSpotlight = (card = null, clientX = null, clientY = null) => {
      const gridRect = grid.getBoundingClientRect();
      const blockRect = block.getBoundingClientRect();
      const cardRect = card?.getBoundingClientRect();
      const fallbackCardRect = cards[0].getBoundingClientRect();
      const width = cardRect ? cardRect.width + 260 : targetSpotlight?.width || fallbackCardRect.width + 260;
      const height = cardRect ? cardRect.height + 220 : targetSpotlight?.height || fallbackCardRect.height + 220;
      const pointerX = clientX === null ? cardRect.left + cardRect.width / 2 : clientX;
      const pointerY = clientY === null ? cardRect.top + cardRect.height / 2 : clientY;
      const visibleRadiusX = width * 0.42;
      const visibleRadiusY = height * 0.42;
      const minimumX = blockRect.left - gridRect.left + visibleRadiusX;
      const maximumX = blockRect.right - gridRect.left - visibleRadiusX;
      const minimumY = blockRect.top - gridRect.top + visibleRadiusY;
      const maximumY = blockRect.bottom - gridRect.top - visibleRadiusY;

      targetSpotlight = {
        x: clamp(pointerX - gridRect.left, minimumX, maximumX),
        y: clamp(pointerY - gridRect.top, minimumY, maximumY),
        width,
        height,
      };

      if (!currentSpotlight) {
        currentSpotlight = { ...targetSpotlight };
        renderSpotlight(currentSpotlight);
      } else if (!animationFrame) {
        previousTime = null;
        animationFrame = window.requestAnimationFrame(animateSpotlight);
      }

      grid.classList.add("has-active-card");
    };

    const setActiveCard = (activeCard) => {
      cards.forEach((card) => card.classList.toggle("is-active", card === activeCard));
    };

    const setInitialState = () => {
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
      animationFrame = null;
      previousTime = null;
      const firstCard = cards[0];
      const gridRect = grid.getBoundingClientRect();
      const cardRect = firstCard.getBoundingClientRect();

      setActiveCard(firstCard);
      targetSpotlight = {
        x: gridRect.width / 2,
        y: gridRect.height / 2,
        width: cardRect.width + 260,
        height: cardRect.height + 220,
      };
      currentSpotlight = { ...targetSpotlight };
      renderSpotlight(currentSpotlight);
      grid.classList.add("has-active-card");
    };

    const handlePointerMove = (event) => {
      if (event.pointerType === "touch") return;

      const hoveredCard = event.target.closest?.("[data-bento-card]");
      const activeCard = hoveredCard && block.contains(hoveredCard) ? hoveredCard : null;

      if (!activeCard) return;
      moveSpotlight(activeCard, event.clientX, event.clientY);
      setActiveCard(activeCard);
    };

    const updatePointerTracking = () => {
      const shouldTrack = isBlockVisible && desktopQuery.matches && !motionQuery.matches && !document.hidden;

      if (shouldTrack === isTrackingPointer) return;
      isTrackingPointer = shouldTrack;

      if (shouldTrack) {
        grid.addEventListener("pointermove", handlePointerMove, { passive: true });
      } else {
        grid.removeEventListener("pointermove", handlePointerMove);
        setInitialState();
      }
    };

    cards.forEach((card) => {
      card.addEventListener("pointerenter", () => {
        if (!isTrackingPointer) return;
        moveSpotlight(card);
        setActiveCard(card);
      });
      card.addEventListener("focusin", () => {
        if (!isTrackingPointer) return;
        moveSpotlight(card);
        setActiveCard(card);
      });
    });

    setInitialState();

    const visibilityObserver = new IntersectionObserver(([entry]) => {
      isBlockVisible = entry.isIntersecting;
      updatePointerTracking();
    });

    visibilityObserver.observe(block);
    const sizeObserver = new ResizeObserver(setInitialState);
    sizeObserver.observe(grid);
    desktopQuery.addEventListener("change", updatePointerTracking);
    motionQuery.addEventListener("change", updatePointerTracking);
    document.addEventListener("visibilitychange", updatePointerTracking);
  });
}
